import React, { useState, useEffect } from 'react';
import { Table, Button, message, Modal, Form, Breadcrumb, Popconfirm, Space, Upload, Progress, Select } from 'antd';
import { HomeOutlined, PlusOutlined, UploadOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import api from '../api';
import ClassForm from '../components/ClassForm';

const ClassManagementPage = () => {
  const [classes, setClasses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [form] = Form.useForm();
  const [importProgress, setImportProgress] = useState({ visible: false, percent: 0, status: 'active' });
  // 批量编辑相关状态
  const [isBatchEditModalVisible, setIsBatchEditModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [batchTeacherId, setBatchTeacherId] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);

  const handleBatchImport = async (file) => {
    try {
      setImportProgress({ visible: true, percent: 0, status: 'active' });

      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/classes/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 50) / progressEvent.total);
          setImportProgress({ visible: true, percent, status: 'active' });
        }
      });

      setImportProgress({ visible: true, percent: 80, status: 'active' });
      await new Promise(resolve => setTimeout(resolve, 300));

      if (response.data.errors && response.data.errors.length > 0) {
        setImportProgress({ visible: true, percent: 100, status: 'exception' });
        Modal.warning({
          title: '导入结果反馈',
          content: (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <p>成功导入: {response.data.successCount} / {response.data.totalCount}</p>
              <p>以下条目导入失败:</p>
              <ul>
                {response.data.errors.map((err, idx) => (
                  <li key={idx} style={{ color: '#ff4d4f' }}>{err}</li>
                ))}
              </ul>
            </div>
          ),
          width: 600
        });
      } else {
        setImportProgress({ visible: true, percent: 100, status: 'success' });
        message.success(`批量导入完成: 成功导入 ${response.data.successCount} 条数据`);
      }

      fetchClasses();

      setTimeout(() => setImportProgress({ visible: false, percent: 0, status: 'active' }), 2000);
    } catch (error) {
      console.error('Batch import error:', error);
      setImportProgress({ visible: true, percent: 100, status: 'exception' });
      const errorMessage = error.response?.data?.error || '批量导入失败，请检查文件格式或网络连接';
      message.error(errorMessage);
      setTimeout(() => setImportProgress({ visible: false, percent: 0, status: 'active' }), 2000);
    }
    return false;
  };

  // 批量编辑功能
  const showBatchEditModal = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要编辑的班级');
      return;
    }
    setBatchTeacherId(null);
    setIsBatchEditModalVisible(true);
  };

  const handleBatchUpdate = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要编辑的班级');
      return;
    }

    setBatchLoading(true);
    try {
      const requests = selectedRowKeys.map(id => {
        return api.put(`/classes/${id}`, { teacherId: batchTeacherId });
      });

      const results = await Promise.allSettled(requests);
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;

      if (failCount > 0) {
        message.warning(`批量更新完成: 成功 ${successCount} 条, 失败 ${failCount} 条`);
      } else {
        message.success(`批量更新成功: 共 ${successCount} 条`);
      }

      setIsBatchEditModalVisible(false);
      setSelectedRowKeys([]);
      setSelectedRows([]);
      setBatchTeacherId(null);
      fetchClasses();
    } catch (error) {
      console.error('批量更新失败:', error);
      message.error('批量更新失败，请重试');
    }
    setBatchLoading(false);
  };

  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const hasSelected = selectedRowKeys.length > 0;

  const teachers = users.filter(u =>
    u.role === 'CLASS_TEACHER' ||
    u.role === 'ADMIN' ||
    u.role === 'TEACHING_ADMIN'
  );

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const response = await api.get('/classes');
      if (Array.isArray(response.data)) {
        setClasses(response.data);
      } else {
        setClasses([]);
        console.error("Expected array from /classes but got:", response.data);
      }
    } catch (error) {
      message.error('获取班级列表失败');
      setClasses([]);
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      // Assuming an endpoint to fetch all users, especially teachers
      const response = await api.get('/users'); 
      if (Array.isArray(response.data)) {
        setUsers(response.data);
      } else {
        setUsers([]);
        console.error("Expected array from /users but got:", response.data);
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
      setUsers([]);
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchUsers();
  }, []);

  const showModal = (record = null) => {
    setEditingClass(record);
    form.setFieldsValue(record ? { ...record, teacherId: record.teacher?.id } : {});
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingClass(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingClass) {
        await api.put(`/classes/${editingClass.id}`, values);
        message.success('班级更新成功');
      } else {
        await api.post('/classes', values);
        message.success('班级创建成功');
      }
      handleCancel();
      fetchClasses();
    } catch (error) {
      message.error('操作失败，请重试');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/classes/${id}`);
      message.success('班级删除成功');
      fetchClasses();
    } catch (error) {
      message.error('删除失败，请重试');
    }
  };

  const columns = [
    { title: '班级名称', dataIndex: 'name', key: 'name', fontWeight: 600 },
    {
      title: '班主任',
      dataIndex: 'teacher',
      key: 'teacher',
      render: (teacher) => teacher?.name || <span style={{ color: '#ccc' }}>未指定</span>,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => showModal(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除该班级吗？"
            description="删除后该班级下的学员将失去班级归属"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" danger size="small">删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <Breadcrumb items={[
          { title: <><HomeOutlined /> 首页</> },
          { title: '教务管理' },
          { title: '班级管理' },
        ]} />
        <h2 className="page-title">班级管理</h2>
      </div>

      <div className="table-card">
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
                    新建班级
                </Button>
                <Upload
                    beforeUpload={handleBatchImport}
                    showUploadList={false}
                    accept=".xlsx, .xls"
                >
                    <Button icon={<UploadOutlined />}>批量导入</Button>
                </Upload>
                <Button type="text" icon={<DownloadOutlined />} href="/template-classes.xlsx" download="班级导入模板.xlsx">
                    下载模板
                </Button>
            </Space>
            <Space>
                {hasSelected && (
                  <span style={{ marginRight: 8 }}>
                    已选择 <a style={{ fontWeight: 600 }}>{selectedRowKeys.length}</a> 项
                  </span>
                )}
                <Button
                  icon={<EditOutlined />}
                  onClick={showBatchEditModal}
                  disabled={!hasSelected}
                >
                    批量修改班主任
                </Button>
                {hasSelected && (
                  <Button onClick={() => { setSelectedRowKeys([]); setSelectedRows([]); }}>
                    取消选择
                  </Button>
                )}
            </Space>
        </div>
        <Table 
            columns={columns} 
            dataSource={classes} 
            loading={loading} 
            rowKey="id" 
            rowSelection={rowSelection}
            pagination={{ 
                defaultPageSize: 10, 
                showTotal: (total) => `共 ${total} 条记录`,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100]
            }}
        />
      </div>

      {importProgress.visible && (
        <div style={{ marginTop: 16, padding: '12px 16px', background: '#fafafa', borderRadius: 8 }}>
          <div style={{ marginBottom: 8, color: '#666' }}>
            {importProgress.status === 'active' && '正在导入...'}
            {importProgress.status === 'success' && '导入完成！'}
            {importProgress.status === 'exception' && '导入完成（部分失败）'}
          </div>
          <Progress
            percent={importProgress.percent}
            status={importProgress.status}
            strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
          />
        </div>
      )}

      <Modal
        title={editingClass ? '编辑班级' : '新建班级'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <ClassForm form={form} users={users} isEditMode={!!editingClass} />
      </Modal>

      <Modal
        title="批量修改班主任"
        open={isBatchEditModalVisible}
        onOk={handleBatchUpdate}
        onCancel={() => { setIsBatchEditModalVisible(false); setBatchTeacherId(null); }}
        confirmLoading={batchLoading}
        okText="确认"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <p style={{ marginBottom: 8 }}>
            即将对 <strong>{selectedRowKeys.length}</strong> 个班级进行批量修改班主任
          </p>
          <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 8 }}>
            {selectedRows.map((cls) => (
              <div key={cls.id} style={{ marginBottom: 4 }}>
                <span>{cls.name}</span>
                {cls.teacher && <span style={{ color: '#888', marginLeft: 8 }}>(当前: {cls.teacher.name})</span>}
              </div>
            ))}
          </div>
        </div>

        <Form layout="vertical">
          <Form.Item label="新班主任">
            <Select
              placeholder="请选择新班主任（留空则清除班主任）"
              value={batchTeacherId}
              onChange={setBatchTeacherId}
              allowClear
              style={{ width: '100%' }}
            >
              {teachers.map(teacher => (
                <Select.Option key={teacher.id} value={teacher.id}>
                  {teacher.name} ({teacher.account})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ClassManagementPage;
